import { useState, useEffect } from "react";
import "./Minesweeper.css";

export default function Minesweeper({ rows = 8, cols = 8, minesCount = 10 }) {
  const [grid, setGrid] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(
    parseInt(localStorage.getItem("minesweeperHighScore")) || 0
  );

  useEffect(() => {
    initGame();
  }, []);

  const initGame = () => {
    const newGrid = Array(rows)
      .fill(null)
      .map(() =>
        Array(cols).fill({
          value: 0,
          revealed: false,
          flagged: false,
        })
      );

    let minesPlaced = 0;
    while (minesPlaced < minesCount) {
      const r = Math.floor(Math.random() * rows);
      const c = Math.floor(Math.random() * cols);
      if (newGrid[r][c].value !== "💣") {
        newGrid[r][c] = { ...newGrid[r][c], value: "💣" };
        minesPlaced++;
      }
    }

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (newGrid[r][c].value === "💣") continue;
        let minesAround = 0;
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const nr = r + dr;
            const nc = c + dc;
            if (nr >= 0 && nr < rows && nc >= 0 && nc < cols && newGrid[nr][nc].value === "💣") {
              minesAround++;
            }
          }
        }
        newGrid[r][c] = { ...newGrid[r][c], value: minesAround };
      }
    }

    setGrid(newGrid);
    setGameOver(false);
    setWon(false);
    setScore(0);
  };

  const revealCell = (r, c) => {
    if (gameOver || grid[r][c].revealed || grid[r][c].flagged) return;

    const newGrid = grid.map((row) => row.map((cell) => ({ ...cell })));
    const reveal = (x, y) => {
      if (
        x < 0 ||
        x >= rows ||
        y < 0 ||
        y >= cols ||
        newGrid[x][y].revealed ||
        newGrid[x][y].flagged
      )
        return;

      newGrid[x][y].revealed = true;

      // Increment score for revealed safe cells
      if (newGrid[x][y].value !== "💣") {
        setScore((prev) => prev + 1);
      }

      if (newGrid[x][y].value === 0) {
        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            if (dx !== 0 || dy !== 0) reveal(x + dx, y + dy);
          }
        }
      }
    };

    reveal(r, c);

    if (newGrid[r][c].value === "💣") {
      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
          if (newGrid[i][j].value === "💣") newGrid[i][j].revealed = true;
        }
      }
      setGameOver(true);
    }

    setGrid(newGrid);
    checkWin(newGrid);
  };

  const toggleFlag = (e, r, c) => {
    e.preventDefault();
    if (gameOver || grid[r][c].revealed) return;

    const newGrid = grid.map((row) => row.map((cell) => ({ ...cell })));
    newGrid[r][c].flagged = !newGrid[r][c].flagged;
    setGrid(newGrid);
  };

  const checkWin = (currentGrid) => {
    const hiddenCells = currentGrid.flat().filter((cell) => !cell.revealed);
    if (hiddenCells.every((cell) => cell.value === "💣")) {
      setWon(true);
      // Save high score
      if (score > highScore) {
        setHighScore(score);
        localStorage.setItem("minesweeperHighScore", score);
      }
    }
  };

  const numberColor = (n) => {
    const colors = ["", "#1976d2", "#388e3c", "#d32f2f", "#303f9f", "#5d4037", "#0097a7", "#000000", "#9e9e9e"];
    return colors[n] || "#000";
  };

  return (
    <div className="minesweeper-wrapper">
      <h1>Minesweeper</h1>

      <div className="rules">
        <h2>Rules:</h2>
        <ul>
          <li>Click a cell to reveal it.</li>
          <li>Right-click to flag/unflag suspected mines.</li>
          <li>Reveal all non-mine cells to win.</li>
          <li>Clicking a mine ends the game.</li>
        </ul>
      </div>

      <div className="scoreboard">
        <p>Score: <strong>{score}</strong></p>
        <p>High Score: <strong>{highScore}</strong></p>
      </div>

      <p className="status">
        {gameOver ? "💥 You Lost!" : won ? "🎉 You Won!" : "Click cells to reveal. Right-click to flag."}
      </p>

      <button onClick={initGame} className="restart-btn">Restart</button>

      <div className="grid">
        {grid.map((row, rIdx) =>
          row.map((cell, cIdx) => (
            <div
              key={`${rIdx}-${cIdx}`}
              className={`cell ${cell.revealed ? "revealed" : ""} ${cell.flagged ? "flagged" : ""}`}
              onClick={() => revealCell(rIdx, cIdx)}
              onContextMenu={(e) => toggleFlag(e, rIdx, cIdx)}
              style={{ color: typeof cell.value === "number" && cell.value !== 0 ? numberColor(cell.value) : "red" }}
            >
              {cell.revealed ? (cell.value === 0 ? "" : cell.value) : cell.flagged ? "🚩" : ""}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
